<template>
<div class="mp-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1100px;">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="mp-back"><i class="mdi mdi-arrow-left"></i></a>
        <div>
          <h4 class="mp-title"><i class="mdi mdi-receipt-text me-2"></i>Platform Invoices</h4>
          <div style="color:#8BA4C4;font-size:12px;">Bills from Simi Trac for platform commission &amp; card fees.</div>
        </div>
      </div>
      <button class="btn mp-btn-blue btn-sm" @click="loadInvoices" :disabled="loading">
        <i class="mdi mdi-refresh me-1"></i>Refresh
      </button>
    </div>

    <!-- Outstanding KPI strip -->
    <div class="d-flex gap-2 mb-2 flex-wrap">
      <div class="mp-stat" style="border-color:#1A3A6A;">
        <div class="mp-stat-lbl">ACCRUED</div>
        <div class="mp-stat-num" style="color:#E8EDF4;">KSh {{ fNum(accrued) }}</div>
        <div class="mp-stat-sub">
          <span style="color:#4FC3F7;">💳 {{ fNum(breakdown.pos_commission.accrued) }}</span>
          <span style="color:#A855F7;margin-left:6px;">🪪 {{ fNum(breakdown.card_fee.accrued) }}</span>
          <span style="color:#F59E0B;margin-left:6px;">✉ {{ fNum(breakdown.sms_bundle.accrued) }}</span>
        </div>
      </div>
      <div class="mp-stat" style="border-color:rgba(16,185,129,.4);">
        <div class="mp-stat-lbl">PAID</div>
        <div class="mp-stat-num" style="color:#10B981;">KSh {{ fNum(settled) }}</div>
        <div class="mp-stat-sub">
          <span style="color:#4FC3F7;">💳 {{ fNum(breakdown.pos_commission.settled) }}</span>
          <span style="color:#A855F7;margin-left:6px;">🪪 {{ fNum(breakdown.card_fee.settled) }}</span>
          <span style="color:#F59E0B;margin-left:6px;">✉ {{ fNum(breakdown.sms_bundle.settled) }}</span>
        </div>
      </div>
      <div class="mp-stat" style="border-color:rgba(245,158,11,.4);">
        <div class="mp-stat-lbl">OUTSTANDING</div>
        <div class="mp-stat-num" :style="{color: outstanding > 0 ? '#F59E0B' : '#8BA4C4'}">KSh {{ fNum(outstanding) }}</div>
        <div class="mp-stat-sub">
          <span style="color:#4FC3F7;">💳 {{ fNum(breakdown.pos_commission.outstanding) }}</span>
          <span style="color:#A855F7;margin-left:6px;">🪪 {{ fNum(breakdown.card_fee.outstanding) }}</span>
          <span style="color:#F59E0B;margin-left:6px;">✉ {{ fNum(breakdown.sms_bundle.outstanding) }}</span>
        </div>
      </div>
    </div>
    <div style="font-size:11px;color:#8BA4C4;margin-bottom:12px;">
      💳 POS Commission &middot; 🪪 Card Fees &middot; ✉ SMS Bundles
    </div>

    <!-- Pay-by-paybill card — only when platform paybill is configured -->
    <div v-if="platformPaybill" class="mp-paybill-card">
      <div style="font-size:12px;color:#8BA4C4;font-weight:600;letter-spacing:.5px;">PAY DIRECTLY VIA M-PESA</div>
      <div style="font-size:13px;color:#E8EDF4;margin-top:6px;line-height:1.5;">
        On your phone: <strong>M-Pesa → Lipa na M-Pesa → Pay Bill</strong>
      </div>
      <div class="d-flex gap-3 mt-2 flex-wrap">
        <div class="mp-pill"><span class="mp-pill-lbl">Business no.</span><span class="mp-pill-val">{{ platformPaybill.shortcode }}</span></div>
        <div class="mp-pill"><span class="mp-pill-lbl">Account</span><span class="mp-pill-val" style="color:#F59E0B;">PINV-####</span></div>
      </div>
      <div style="font-size:11px;color:#8BA4C4;margin-top:6px;">
        Use the invoice number (e.g. <code style="color:#4FC3F7;">PINV-2026-0001</code>) as the account reference. Payment auto-matches and clears the invoice.
      </div>
    </div>

    <!-- Invoices list -->
    <div v-if="loading" class="mp-spinner"><div class="spinner-border" style="color:#4FC3F7;"></div></div>
    <div v-else-if="invoices.length" style="overflow-x:auto;" class="mp-card">
      <table class="mp-table">
        <thead><tr>
          <th>Invoice #</th><th>Period</th><th>Lines</th>
          <th style="color:#4FC3F7;">💳 Commission</th>
          <th style="color:#A855F7;">🪪 Card Fees</th>
          <th>Amount</th><th>Paid</th><th>Status</th><th>Issued</th><th></th>
        </tr></thead>
        <tbody>
          <tr v-for="i in invoices" :key="i.id" :style="i.status === 'void' ? 'opacity:.5;' : ''">
            <td style="font-weight:700;font-family:monospace;color:#4FC3F7;">{{ i.invoice_no }}</td>
            <td style="font-size:12px;">{{ i.period_start.substr(0, 10) }} → {{ i.period_end.substr(0, 10) }}</td>
            <td>{{ i.line_count }}</td>
            <td style="font-size:12px;color:#4FC3F7;font-weight:600;">KSh {{ fNum(i.breakdown && i.breakdown.pos_commission ? i.breakdown.pos_commission.amount : 0) }}</td>
            <td style="font-size:12px;color:#A855F7;font-weight:600;">KSh {{ fNum(i.breakdown && i.breakdown.card_fee ? i.breakdown.card_fee.amount : 0) }}</td>
            <td style="font-weight:700;">KSh {{ fNum(i.amount) }}</td>
            <td :style="{color: Number(i.paid_amount) >= Number(i.amount) ? '#10B981' : (Number(i.paid_amount) > 0 ? '#F59E0B' : '#8BA4C4')}">KSh {{ fNum(i.paid_amount) }}</td>
            <td>
              <span class="mp-badge" :class="badgeClass(i.status)">{{ i.status }}</span>
              <div v-if="i.status === 'partial'" style="font-size:10px;color:#F59E0B;margin-top:2px;">−KSh {{ fNum(i.outstanding) }}</div>
            </td>
            <td style="font-size:11px;color:#8BA4C4;">{{ i.issued_at ? i.issued_at.substr(0, 10) : '—' }}</td>
            <td style="white-space:nowrap;">
              <button v-if="i.status !== 'void' && Number(i.outstanding) > 0" class="btn mp-btn-success btn-sm me-1" @click="openPay(i)" :disabled="!platformPaybill">
                <i class="mdi mdi-cellphone-arrow-down me-1"></i>Pay
              </button>
              <span v-else-if="i.status === 'paid'" style="color:#10B981;font-size:12px;font-weight:600;margin-right:6px;">✓ Paid</span>
              <span v-else-if="i.status === 'void'" style="color:#EF4444;font-size:11px;margin-right:6px;">Voided</span>
              <button class="btn mp-btn-blue btn-sm" @click="printInvoice(i)" title="Print or download invoice as PDF">
                <i class="mdi mdi-printer"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="mp-empty">
      <i class="mdi mdi-receipt-text-outline" style="font-size:42px;color:#4A6A8A;display:block;margin-bottom:8px;"></i>
      No invoices yet. They'll appear here when Simi Trac issues them.
    </div>

    <!-- ============ PAY MODAL — STK push ============ -->
    <div class="mp-overlay" v-if="showPay" @click.self="closePay">
      <div class="mp-modal">
        <div class="mp-modal-title">
          <span><i class="mdi mdi-cellphone-arrow-down me-2"></i>Pay {{ payForm.invoiceNo }}</span>
          <button class="mp-modal-close" @click="closePay">&times;</button>
        </div>
        <div class="mp-alert-danger" v-if="payForm.error">{{ payForm.error }}</div>

        <div style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:8px;padding:12px;margin-bottom:14px;">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:#8BA4C4;margin-bottom:6px;">
            <span>Outstanding</span><span style="color:#F59E0B;font-weight:700;font-size:16px;">KSh {{ fNum(payForm.outstanding) }}</span>
          </div>
          <div style="font-size:11px;color:#8BA4C4;">Period: {{ payForm.period }}</div>
        </div>

        <div v-if="payForm.stage === 'compose'">
          <label class="mp-label">Phone (Safaricom)</label>
          <input class="mp-input" v-model="payForm.phone" placeholder="0712345678" />
          <small style="color:#8BA4C4;font-size:11px;">An M-Pesa prompt will be sent to this number for KSh {{ fNum(payForm.outstanding) }}.</small>

          <div class="d-flex gap-2 mt-3">
            <button class="btn mp-btn-success flex-fill" @click="sendStk" :disabled="payForm.sending || !payForm.phone">
              <span v-if="payForm.sending" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-cellphone-arrow-down me-1"></i>{{ payForm.sending ? 'Sending…' : 'Send M-Pesa Prompt' }}
            </button>
            <button class="btn mp-btn-ghost" @click="closePay">Cancel</button>
          </div>
        </div>

        <div v-else-if="payForm.stage === 'pending'">
          <div style="text-align:center;padding:20px;">
            <div class="spinner-border" style="color:#4FC3F7;width:40px;height:40px;border-width:4px;"></div>
            <div style="color:#E8EDF4;margin-top:12px;font-weight:600;">📱 Check your phone</div>
            <div style="color:#8BA4C4;font-size:13px;margin-top:4px;">Enter your M-Pesa PIN on the prompt for {{ payForm.phone }}.</div>
            <div style="color:#4FC3F7;font-size:12px;margin-top:12px;">Waiting for confirmation… {{ payForm.elapsed }}s</div>
          </div>
          <button class="btn mp-btn-ghost" @click="closePay" style="width:100%;margin-top:8px;">Close (it'll continue in the background)</button>
        </div>

        <div v-else-if="payForm.stage === 'paid'">
          <div style="text-align:center;padding:20px;">
            <div style="font-size:50px;color:#10B981;">✓</div>
            <div style="color:#10B981;font-size:18px;font-weight:800;margin-top:8px;">Payment received!</div>
            <div style="color:#8BA4C4;font-size:13px;margin-top:6px;">{{ payForm.successMsg }}</div>
          </div>
          <button class="btn mp-btn-success" @click="closePay" style="width:100%;">Done</button>
        </div>

        <div v-else-if="payForm.stage === 'failed'">
          <div style="text-align:center;padding:20px;">
            <div style="font-size:50px;color:#EF4444;">✕</div>
            <div style="color:#EF4444;font-size:16px;font-weight:700;margin-top:8px;">{{ payForm.failedReason }}</div>
            <div style="color:#8BA4C4;font-size:12px;margin-top:6px;">Try again, or pay manually using the paybill instructions on the page.</div>
          </div>
          <button class="btn mp-btn-blue" @click="payForm.stage = 'compose'" style="width:100%;">Try Again</button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
import { printPlatformInvoice } from "@/utils/printPlatformInvoice";

export default {
  name: 'MyPlatformInvoices',
  components: { Navigation },
  data() {
    return {
      loading: true,
      invoices: [],
      accrued: 0, settled: 0, outstanding: 0,
      breakdown: { pos_commission: { accrued: 0, settled: 0, outstanding: 0 }, card_fee: { accrued: 0, settled: 0, outstanding: 0 }, sms_bundle: { accrued: 0, settled: 0, outstanding: 0 } },
      platformPaybill: null,
      // Pay modal
      showPay: false,
      payForm: {
        invoiceId: null, invoiceNo: '', outstanding: 0, period: '',
        phone: '',
        stage: 'compose',                       // compose | pending | paid | failed
        sending: false, error: '', successMsg: '', failedReason: '',
        elapsed: 0,
        checkoutId: null, pollHandle: null, elapsedHandle: null,
      },
    };
  },
  mounted() {
    // Pre-fill phone from logged-in user if available
    const phone = sessionStorage.getItem('userPhone') || '';
    this.payForm.phone = phone;
    this.loadInvoices();
  },
  beforeUnmount() { this.stopPolling(); },
  methods: {
    fNum(v) { if (!v && v !== 0) return '0'; return Number(v).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    badgeClass(s) {
      return s === 'paid' ? 'mp-badge-success'
           : s === 'partial' ? 'mp-badge-warning'
           : s === 'void' ? 'mp-badge-danger'
           : 'mp-badge-info';
    },
    async printInvoice(i) {
      try {
        const r = await executeJson({ function: 'getPlatformInvoiceDetails', invoiceId: i.id }, simitrack);
        if (r.data.success) printPlatformInvoice(r.data.data);
        else alert(r.data.message || 'Could not load invoice');
      } catch (e) { alert('Connection error'); }
    },
    async loadInvoices() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'getMyPlatformInvoices' }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.invoices = d.invoices || [];
          this.accrued = Number(d.accrued || 0);
          this.settled = Number(d.settled || 0);
          this.outstanding = Number(d.outstanding || 0);
          if (d.breakdown) this.breakdown = d.breakdown;
          this.platformPaybill = d.platform_paybill || null;
        }
      } catch (e) { /* silent */ }
      this.loading = false;
    },
    openPay(invoice) {
      this.payForm = {
        ...this.payForm,
        invoiceId: invoice.id,
        invoiceNo: invoice.invoice_no,
        outstanding: Number(invoice.outstanding),
        period: invoice.period_start.substr(0, 10) + ' → ' + invoice.period_end.substr(0, 10),
        stage: 'compose',
        sending: false, error: '', successMsg: '', failedReason: '',
        elapsed: 0,
        phone: this.payForm.phone || sessionStorage.getItem('userPhone') || '',
      };
      this.showPay = true;
    },
    closePay() {
      this.stopPolling();
      this.showPay = false;
    },
    stopPolling() {
      if (this.payForm.pollHandle) { clearInterval(this.payForm.pollHandle); this.payForm.pollHandle = null; }
      if (this.payForm.elapsedHandle) { clearInterval(this.payForm.elapsedHandle); this.payForm.elapsedHandle = null; }
    },
    async sendStk() {
      const f = this.payForm;
      f.error = '';
      if (!f.phone || f.phone.length < 9) { f.error = 'Valid phone number required'; return; }
      f.sending = true;
      try {
        const r = await executeJson({
          function: 'payPlatformInvoiceStk',
          invoiceId: f.invoiceId,
          phone: f.phone,
          amount: f.outstanding,
        }, simitrack);
        if (r.data.success) {
          f.checkoutId = r.data.data && r.data.data.checkout_request_id;
          f.stage = 'pending';
          f.elapsed = 0;
          // Poll STK status every 3s, time out after ~90s
          f.elapsedHandle = setInterval(() => { f.elapsed += 1; }, 1000);
          f.pollHandle = setInterval(() => this.pollStk(), 3000);
        } else {
          f.error = r.data.message || 'Failed to send prompt';
        }
      } catch (e) { f.error = 'Connection error'; }
      f.sending = false;
    },
    async pollStk() {
      const f = this.payForm;
      if (!f.checkoutId) return;
      if (f.elapsed > 90) {
        // Soft-timeout — keep the UI usable but stop polling. Server-side state is authoritative;
        // user can reload the list after to verify.
        this.stopPolling();
        f.failedReason = 'No response yet — check your phone or wait a moment and refresh the invoice list.';
        f.stage = 'failed';
        return;
      }
      try {
        const r = await executeJson({ function: 'checkStkStatus', checkoutRequestId: f.checkoutId }, simitrack);
        const d = r.data && r.data.data;
        if (!d) return;
        if (d.status === 'completed') {
          this.stopPolling();
          f.successMsg = (r.data.message || '') + ' ' + (d.provider_ref ? `M-Pesa: ${d.provider_ref}` : '');
          f.stage = 'paid';
          await this.loadInvoices();
        } else if (d.status === 'failed' || d.status === 'cancelled') {
          this.stopPolling();
          f.failedReason = r.data.message || 'Payment did not complete';
          f.stage = 'failed';
        }
      } catch (e) { /* keep polling */ }
    },
  },
};
</script>

<style scoped>
.mp-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.mp-back{color:#8BA4C4;font-size:22px;text-decoration:none;}.mp-back:hover{color:#4FC3F7;}
.mp-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}

.mp-stat{flex:1;min-width:160px;background:#0F2347;border:1px solid;border-radius:10px;padding:12px 14px;}
.mp-stat-lbl{font-size:10px;color:#8BA4C4;font-weight:700;letter-spacing:.5px;}
.mp-stat-num{font-size:20px;font-weight:800;margin-top:4px;}
.mp-stat-sub{font-size:11px;font-weight:600;margin-top:4px;}

.mp-paybill-card{background:rgba(16,185,129,.05);border:1px solid rgba(16,185,129,.3);border-radius:10px;padding:14px;margin-bottom:14px;}
.mp-pill{background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;display:flex;flex-direction:column;gap:2px;}
.mp-pill-lbl{font-size:10px;color:#8BA4C4;font-weight:700;letter-spacing:.5px;}
.mp-pill-val{font-size:16px;font-weight:800;color:#E8EDF4;font-family:monospace;}

.mp-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:16px;}
.mp-table{width:100%;border-collapse:collapse;color:#E8EDF4;font-size:13px;}
.mp-table th{color:#8BA4C4;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.mp-table td{padding:12px;border-bottom:1px solid rgba(26,58,106,.4);}
.mp-table tr:hover td{background:rgba(46,134,222,.04);}
.mp-empty{text-align:center;padding:60px 20px;background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;color:#8BA4C4;}

.mp-badge{display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.3px;text-transform:uppercase;}
.mp-badge-success{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.4);}
.mp-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.4);}
.mp-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.4);}
.mp-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.4);}

.mp-btn-blue{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.mp-btn-success{background:linear-gradient(135deg,#10B981,#059669);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.mp-btn-ghost{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:8px;padding:8px 16px;font-size:13px;}

.mp-spinner{display:flex;justify-content:center;padding:40px;}

.mp-overlay{position:fixed;inset:0;background:rgba(5,10,20,.85);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1050;padding:20px;}
.mp-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,.5);width:100%;max-width:480px;padding:20px;}
.mp-modal-title{display:flex;align-items:center;justify-content:space-between;color:#E8EDF4;font-size:16px;font-weight:700;padding-bottom:12px;border-bottom:1px solid #1A3A6A;margin-bottom:14px;}
.mp-modal-close{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;width:30px;height:30px;border-radius:8px;font-size:14px;cursor:pointer;line-height:1;}
.mp-label{display:block;color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;}
.mp-input{background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:14px;outline:none;width:100%;}
.mp-input:focus{border-color:#2E86DE;}
.mp-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.3);color:#EF4444;padding:10px 12px;border-radius:8px;font-size:13px;margin-bottom:12px;}
</style>
